import { createContext, useContext, useMemo } from "react";
import { useLocation } from "wouter";
import { MainFooter } from "@/components/Common/MainFooter/MainFooter";
import { useAuthState } from "@/store/auth";
import { useUserStore } from "@/store/user";

/** True for descendants of the app shell `<PageWrapper includeMainFooter>` (route output). */
const InsideAppPageShellContext = createContext(false);

type PageWrapperProps = {
  children: React.ReactNode;
  testId?: string;
  /** App shell only: render `MainFooter` after children (see `InsideAppPageShellContext`). */
  includeMainFooter?: boolean;
};

export const PageWrapper: React.FC<PageWrapperProps> = ({ children, testId, includeMainFooter = false }) => {
  const insideAppShell = useContext(InsideAppPageShellContext);
  const [location] = useLocation();
  const isLoggedIn = useAuthState((state) => state.status) === "authenticated";
  const theme = useUserStore((state) => state.theme);
  const effectiveTheme = isLoggedIn ? theme : "light";

  const isFooterVisible = useMemo(() => {
    const hideFooterRoutes = [
      { path: "/ai/models", mustAlsoInclude: "/chat" },
      { path: "/ai/re" },
      { path: "/ai/app" },
      { path: "/ai/agentic-workflow-editor" },
      { path: "/ai/training" },
      { path: "/cloud/virtual-machines/create-cluster" },
      { path: "/cloud/container/create-cluster" },
      { path: "/cloud/container/", mustAlsoInclude: "/logs" },
      { path: "/cloud/kubernetes/create-cluster" }
    ];

    for (const route of hideFooterRoutes) {
      if (location.includes(route.path) && (!route.mustAlsoInclude || location.includes(route.mustAlsoInclude))) {
        return false;
      }
    }
    return true;
  }, [location]);

  if (insideAppShell) {
    return <>{children}</>;
  }

  const shellClassName = `flex min-h-0 flex-1 flex-col justify-stretch ${effectiveTheme === "dark" ? "dark" : "light"
    }`;

  if (includeMainFooter) {
    return (
      <InsideAppPageShellContext.Provider value={true}>
        <div className={shellClassName} data-testid={testId}>
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">{children}</div>
          {/* {isFooterVisible ? <MainFooter /> : null} */}
        </div>
      </InsideAppPageShellContext.Provider>
    );
  }

  return (
    <div className={shellClassName} data-testid={testId}>
      {children}
    </div>
  );
};
